import React, { SyntheticEvent, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';

import Authorized from 'Authorized/Authorized';
import clsx from 'clsx';
import { ILinkConfig } from 'types';

import { Links } from './LINKS_LIST';

const useStyles = makeStyles({
	navSideBar: {
		height: '88vh',
		overflowX: 'hidden',
		'&::-webkit-scrollbar': {
			display: 'none',
		},
	},
	rightIcon: {
		display: 'flex',
	},
	wrapText: {
		whiteSpace: 'pre-wrap',
	},
	icon: {
		color: 'white',
	},
	divider: {
		background: '#EEECF91A',
	},
	menu: {
		'& .MuiMenu-paper': {
			background: '#1D1D1B',
		},
	},
	activeTab: {
		borderLeft: '5px solid #00afcb',
	},
	activeTextColor: {
		color: '#00afcb',
	},
	inactiveText: {
		color: '#fff',
	},
});

const isActiveTab = (path: string, href?: string) => path === href;

const SideBarLinkItem: React.FC<{ data: ILinkConfig; isSub?: boolean }> = ({
	data: { href, text, Icon, sublist, permission, isRedirect },
	isSub = false,
}) => {
	const classes = useStyles();
	const location = useLocation();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const handleRequestOpen = (event: SyntheticEvent<HTMLElement>) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};
	const handleRequestClose = () => setAnchorEl(null);

	if (Array.isArray(sublist)) {
		const isActive = () =>
			isActiveTab(location.pathname, href) || sublist.some((item) => isActiveTab(location.pathname, item.href));

		const idMenu = `hoverable-menu-${text}`;
		return (
			<Authorized to={permission}>
				<>
					<Tooltip title={text} placement="right">
						<ListItem
							button
							className={isActive() ? classes.activeTab : ''}
							onClick={handleRequestOpen}
							aria-owns={anchorEl !== null ? idMenu : undefined}
							aria-haspopup="true">
							<ListItemIcon classes={{ root: classes.icon }}>{Icon && <Icon />}</ListItemIcon>
							<Grid alignItems="center" justify="space-between" wrap="nowrap" container>
								<ListItemText
									primary={text}
									className={clsx(
										classes.wrapText,
										isActive() ? classes.activeTextColor : classes.inactiveText,
									)}
								/>
								<KeyboardArrowRightIcon
									className={classes.rightIcon}
									htmlColor={isActive() ? '#00afcb' : '#fff'}
								/>
							</Grid>
						</ListItem>
					</Tooltip>
					<Menu
						id={idMenu}
						anchorEl={anchorEl}
						open={Boolean(anchorEl)}
						onClose={handleRequestClose}
						keepMounted
						className={classes.menu}
						onClick={handleRequestClose}>
						{sublist.map((item) => (
							<SideBarLinkItem key={item.text} data={item} isSub={true} />
						))}
					</Menu>
					<Divider className={classes.divider} />
				</>
			</Authorized>
		);
	}

	const Component = isRedirect ? 'a' : NavLink;

	return (
		<Authorized to={permission}>
			<Tooltip
				title={text}
				disableFocusListener={isSub}
				disableHoverListener={isSub}
				disableTouchListener={isSub}
				placement="right">
				<ListItem
					component={Component}
					href={href}
					to={href}
					style={{ color: 'currentcolor' }}
					button
					className={isActiveTab(location.pathname, href) ? classes.activeTab : ''}>
					{Icon && (
						<ListItemIcon classes={{ root: classes.icon }}>
							<Icon />
						</ListItemIcon>
					)}
					<ListItemText
						primary={text}
						className={clsx(
							classes.wrapText,
							isActiveTab(location.pathname, href) ? classes.activeTextColor : classes.inactiveText,
						)}
					/>
				</ListItem>
			</Tooltip>
			<Divider className={classes.divider} />
		</Authorized>
	);
};

const SideBarLinks: React.FC = () => {
	const classes = useStyles();
	return (
		<List component="nav" className={classes.navSideBar}>
			{Links.map((item) => (
				<SideBarLinkItem key={item.text} data={item} />
			))}
		</List>
	);
};

export default React.memo(SideBarLinks);
